import React, { useMemo } from "react"
import { css, SerializedStyles } from "@emotion/react"
import { Skeleton } from "@material-ui/lab"
import { Box, BoxProps } from "../layout"

export interface PartImageProps extends BoxProps {
	src?: string | null
	alt?: string | null
	isLoading?: boolean
	imageCss?: SerializedStyles
	skeleton?: {
		size?: number
		containerProps?: BoxProps
	}
	showBorder?: boolean
	objectPosition?: "top" | "right" | "bottom" | "left" | string
	isPreview?: boolean
	imgHeight?: number
	imgWidth?: number
}

export const PartImage: React.FC<PartImageProps> = ({
	src,
	alt = "Coming soon part",
	height,
	minHeight,
	maxHeight,
	width,
	minWidth = "3rem",
	maxWidth,
	objectPosition = "initial",
	display = "block",
	isLoading = false,
	skeleton = {},
	showBorder = false,
	imageCss,
	isPreview,
	imgHeight,
	imgWidth,
	...rest
}) => {
	const ComingSoon = "/parts/coming-soon.svg"

	const style = useMemo(
		() => css`
			border: ${showBorder ? "1px solid #eee" : "none"};
			img {
				display: inline;
				border-radius: 4px;
				object-fit: contain;
				object-position: ${objectPosition};
				height: 100%;
				width: 100%;
			}
		`,
		[objectPosition, showBorder]
	)

	const handleImgErr = (event: React.SyntheticEvent<HTMLImageElement>) => {
		event.currentTarget.onerror = null // prevent infinite loop
		event.currentTarget.src = ComingSoon
	}

	const renderImage = () => (
		<img
			src={src || ComingSoon}
			alt={alt ?? ""}
			onError={handleImgErr}
			height={imgHeight ?? "auto"}
			width={imgWidth ?? "auto"}
			loading="lazy"
		/>
	)

	if (isPreview) {
		return (
			<img
				src={src || ComingSoon}
				alt={alt ?? ""}
				css={itemImage}
				onError={handleImgErr}
				height={imgHeight ?? "auto"}
				width={imgWidth ?? "auto"}
				loading="lazy"
			/>
		)
	}

	return (
		<Box
			display={display}
			css={style}
			height={height}
			minHeight={minHeight}
			maxHeight={maxHeight}
			width={width}
			minWidth={minWidth}
			maxWidth={maxWidth}
			{...rest}
		>
			<div css={[imageZoomContainerStyle, imageCss]}>
				{isLoading ?
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						{...skeleton.containerProps}
					>
						<Skeleton
							variant="circle"
							height={skeleton.size ?? 100}
							width={skeleton.size ?? 100}
						/>
					</Box>
				:	renderImage()}
			</div>
		</Box>
	)
}

const imageZoomContainerStyle = css`
	overflow: hidden;
	object-fit: cover;
	align-items: center;
	max-width: 30rem;
	margin: 0 auto;
	text-align: center;
`

const itemImage = css`
	border-radius: 0.37rem;
	border: 1px solid #e5e6e6;
	padding: 0.5rem;
	width: 5.6rem;
	height: 5.6rem;
	max-width: 5.6rem;
	max-height: 5.6rem;
	min-width: 5.6rem;
	min-height: 5.6rem;
	margin-right: 1rem;
`
